import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CustomerIcons from '@/icons/CustomerIcons'
import EyeIcons from '@/icons/EyeIcons'
import PersonRemoveIcons from '@/icons/PersonRemoveIcons'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import useDebounce from '@/hooks/useDebounce'
import ContactDetailModal from '../../../../../modals/ContactMarketingModals/Detail'
import {
    convertToHRContactSource,
    deleteContactSource,
    getContactSource,
    markSpamContactSource,
} from '../../../services'
import { ContactSource } from '../../../types/response'
import { contactSourceColumns } from '../column'
import { useLeadFilters, encodeFilters } from '../LeadFiltersContext'
import {
    DataTableCustomized,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import type { SortOrder } from '@/components/ui/data-table-customized'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import Paper from '@/components/Paper'

const PAGE_SIZE = 10

const AllLeadsList: React.FC = () => {
    const { t } = useTranslation()
    const { t: tMar } = useTranslation('marketing')
    const [searchParams, setSearchParams] = useSearchParams()
    const { filters } = useLeadFilters()
    const { setConfirmation } = useConfirmation()

    const [selectedContactId, setSelectedContactId] = useState<number | null>(
        null
    )
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    // Search state with debounce
    const [searchInput, setSearchInput] = useState('')
    const debouncedSearch = useDebounce(searchInput, 300)

    // Pagination & sorting state
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)
    const [sortBy, setSortBy] = useState('contact_source_created_at')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

    // Check URL params on mount to auto-open modal
    useEffect(() => {
        const contactIdFromUrl = searchParams.get('contact_source_id')
        if (contactIdFromUrl) {
            const contactId = Number(contactIdFromUrl)
            if (!isNaN(contactId)) {
                setSelectedContactId(contactId)
                setIsModalOpen(true)
            }
        }
    }, [searchParams])

    const { mutateAsync: deleteContactSourceMutation } = useMutationWithHandler(
        { mutationFn: deleteContactSource },
        { reactQueryKeys: QUERY_KEYS.MARKETING }
    )

    const { mutateAsync: convertToHRContactSourceMutation } =
        useMutationWithHandler(
            { mutationFn: convertToHRContactSource },
            { reactQueryKeys: QUERY_KEYS.MARKETING }
        )

    const { mutateAsync: markSpamContactSourceMutation } =
        useMutationWithHandler(
            { mutationFn: markSpamContactSource },
            { reactQueryKeys: QUERY_KEYS.MARKETING }
        )

    // Build API payload with encoded filters (base64)
    const queryPayload = useMemo(() => {
        const { ...encodableFilters } = filters
        return {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
            q: debouncedSearch || '',
            filters: encodeFilters(encodableFilters) || undefined,
        }
    }, [page, pageSize, sortBy, sortOrder, debouncedSearch, filters])

    const { data, isLoading, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MARKETING, 'contact-source-list', queryPayload],
        queryFn: () => getContactSource(queryPayload),
    })

    const contacts = data?.results || []
    const total = data?.pagination?.total || 0

    // Reset page when filters change
    useEffect(() => {
        setPage(1)
    }, [filters])

    const handleOpenContact = useCallback(
        (id: number) => {
            setSelectedContactId(id)
            setIsModalOpen(true)
            const currentParams = Object.fromEntries(searchParams.entries())
            setSearchParams({
                ...currentParams,
                contact_source_id: id.toString(),
            })
        },
        [searchParams, setSearchParams]
    )

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
        setSelectedContactId(null)
        const currentParams = Object.fromEntries(searchParams.entries())
        delete currentParams.contact_source_id
        setSearchParams(currentParams)
    }, [searchParams, setSearchParams])

    const handleDelete = async (keys: string[]) => {
        const results = await Promise.allSettled(
            keys.map((id) =>
                deleteContactSourceMutation({ contact_source_id: Number(id) })
            )
        )
        const successful = results.filter(
            (r) => r.status === 'fulfilled'
        ).length
        if (successful > 0) {
            toast.success(t('delete-contact-source-success'))
            setSelectedKeys([])
        }
    }

    const handleConvertToHR = (id: number, name: string) => {
        setConfirmation({
            title: 'Convert To HR',
            description: `${t('convert-description')} "${id} - ${name}" to HR?`,
            onConfirm: async () => {
                await convertToHRContactSourceMutation({
                    contact_source_id: id,
                })
                toast.success(t('convert-customer-to-hr-success'))
            },
            open: true,
        })
    }

    const handleMarkSpam = (id: number, name: string) => {
        setConfirmation({
            title: 'Mark Spam',
            description: `${t('mark-spam')} "${id} - ${name}"?`,
            onConfirm: async () => {
                await markSpamContactSourceMutation({ contact_source_id: id })
                toast.success(t('mark-spam-success'))
            },
            open: true,
        })
    }

    const columns = useMemo(
        () => contactSourceColumns(tMar, t, handleOpenContact),
        [tMar, t, handleOpenContact]
    )

    const actions: DataTableAction<ContactSource>[] = useMemo(
        () => [
            {
                key: 'view',
                label: t('view'),
                icon: <EyeIcons />,
                variant: 'info' as const,
                onClick: (row) => handleOpenContact(row?.contact_source_id),
            },
            {
                key: 'convert-hr',
                label: t('convert-to-hr'),
                icon: <CustomerIcons />,
                variant: 'warning' as const,
                onClick: (row) =>
                    handleConvertToHR(
                        row?.contact_source_id,
                        row?.contact_source_name
                    ),
                hidden: (row) => row.contact_source_status !== 'UNVERIFIED',
            },
            {
                key: 'mark-spam',
                label: t('markspam'),
                icon: <PersonRemoveIcons />,
                variant: 'danger' as const,
                onClick: (row) =>
                    handleMarkSpam(
                        row?.contact_source_id,
                        row?.contact_source_name
                    ),
                hidden: (row) => row.contact_source_status === 'MATCHED',
            },
        ],
        [t, handleOpenContact]
    )

    const getRowClassName = useCallback((row: ContactSource) => {
        if (row?.contact_source_status === 'FOLLOWING') return 'bg-info'
        if (
            ['UNVERIFIED', 'UN-MATCHED'].includes(
                row?.contact_source_status ?? ''
            )
        )
            return 'bg-warning'
        return ''
    }, [])

    return (
        <>
            <Paper>
                <DataTableCustomized<ContactSource>
                    columns={columns}
                    rows={contacts}
                    loading={isLoading}
                    isFetching={isFetching}
                    rowKey={(row) => String(row.contact_source_id)}
                    onRowClick={(row) =>
                        handleOpenContact(row.contact_source_id)
                    }
                    rowClassName={getRowClassName}
                    autoWidth
                    actions={actions}
                    toolbar={{
                        search: {
                            value: searchInput,
                            onChange: setSearchInput,
                            placeholder: t('contact-search-mkt'),
                        },
                    }}
                    selection={{
                        selectedKeys,
                        onSelectionChange: setSelectedKeys,
                        getRowKey: (row) => String(row.contact_source_id),
                        canSelect: (row) =>
                            ['UNCLASSIFIED', 'SPAM'].includes(
                                row?.contact_source_status ?? ''
                            ),
                        actions: [
                            {
                                key: 'delete',
                                label: `${t('contact-source-delete')} (${selectedKeys.length})`,
                                icon: <DeleteOutlineIcons />,
                                variant: 'danger',
                                onClick: () => {
                                    setConfirmation({
                                        title: t('contact-source-delete-title'),
                                        description: t(
                                            'contact-source-delete-description'
                                        ),
                                        onConfirm: () =>
                                            handleDelete(selectedKeys),
                                        open: true,
                                    })
                                },
                            },
                        ],
                    }}
                    sorting={{
                        sortBy,
                        sortOrder,
                        onSortChange: (newSortBy, newSortOrder) => {
                            setSortBy(newSortBy)
                            setSortOrder(newSortOrder)
                        },
                    }}
                    pagination={{
                        page,
                        pageSize,
                        total,
                        onPageChange: setPage,
                        onPageSizeChange: (size) => {
                            setPageSize(size)
                            setPage(1)
                        },
                        pageSizeOptions: [10, 20, 50],
                    }}
                />
            </Paper>
            <ContactDetailModal
                open={isModalOpen}
                onClose={handleCloseModal}
                contactSourceID={selectedContactId}
            />
        </>
    )
}

export default AllLeadsList
